import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { UserContext } from './userContext';

export const NotificationContext = createContext();

const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const { currentUser } = useContext(UserContext);

  useEffect(() => {
    if (currentUser?.token) {
      fetchNotifications();
      const interval = setInterval(fetchUnreadCount, 60000); // Check every minute
      return () => clearInterval(interval);
    }
  }, [currentUser]);

  const fetchNotifications = async () => {
    if (!currentUser?.token) return;
    
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/notifications`,
        {
          headers: { Authorization: `Bearer ${currentUser.token}` }
        }
      );
      setNotifications(response.data.notifications);
      setUnreadCount(response.data.pagination.unread);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUnreadCount = async () => {
    if (!currentUser?.token) return;
    
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/notifications/unread-count`,
        {
          headers: { Authorization: `Bearer ${currentUser.token}` }
        }
      );
      setUnreadCount(response.data.count);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const markAsRead = async (notificationId) => {
    if (!currentUser?.token) return;
    
    try {
      await axios.patch(
        `${process.env.REACT_APP_BASE_URL}/notifications/${notificationId}/read`,
        {},
        {
          headers: { Authorization: `Bearer ${currentUser.token}` }
        }
      );
      
      // Update local state
      setNotifications(prev => 
        prev.map(notification => 
          notification._id === notificationId 
            ? { ...notification, read: true } 
            : notification
        )
      );
      
      // Decrease unread count
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    if (!currentUser?.token) return;
    
    try {
      await axios.patch(
        `${process.env.REACT_APP_BASE_URL}/notifications/read-all`,
        {},
        {
          headers: { Authorization: `Bearer ${currentUser.token}` }
        }
      );
      
      // Update local state
      setNotifications(prev => 
        prev.map(notification => ({ ...notification, read: true }))
      );
      
      // Reset unread count
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const deleteNotification = async (notificationId) => {
    if (!currentUser?.token) return;
    
    try {
      await axios.delete(
        `${process.env.REACT_APP_BASE_URL}/notifications/${notificationId}`,
        {
          headers: { Authorization: `Bearer ${currentUser.token}` }
        }
      );
      
      // Update local state
      setNotifications(prev => 
        prev.filter(notification => notification._id !== notificationId)
      );
      
      // If it was unread, decrease unread count
      const wasUnread = notifications.find(n => n._id === notificationId && !n.read);
      if (wasUnread) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  return (
    <NotificationContext.Provider 
      value={{ 
        notifications, 
        unreadCount, 
        loading, 
        fetchNotifications, 
        markAsRead, 
        markAllAsRead, 
        deleteNotification 
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationProvider;